import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DatabaseService } from '../../../services/database.service';
import { environment } from '../../../services/global-environment';
import { map, catchError, finalize } from 'rxjs/operators';
import { of } from 'rxjs';
import { AttachmentFile, ControlItem as ControlItemModel, ControlItemQueryParams } from '../../../models/control-item.model';
import { ControlItemService } from '../../../services/control-item.service';
import { AuthService } from '../../../services/auth.service';
import { GuidService } from '../../../services/guid.service';
import { FileUploadService } from '../../../services/file-upload.service';
import { HttpEventType } from '@angular/common/http';
import { I18nService } from '../../../services/i18n.service';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { AssistantUrlService } from '../../../services/assistant-url.service';
import { ComponentPermissionService } from '../../../services/component-permission.service';
import { PermissionBaseComponent } from '../../base/permission-base.component';
import { DepartmentService } from '../../../services/department.service';
import { Department } from '../../../models/department.model';

interface ControlItem {
  id: string; // ID 統一使用字串格式 (GUID)
  companyid: string;
  assistantNo: string;
  title: string;
  description: string;
  dueDate: string;
  assignedTo: string;
  departmentId?: string; // 所屬部門 ID
  departmentName?: string; // 所屬部門名稱
  priority: 'low' | 'medium' | 'high';
  status: 'pending' | 'inProgress' | 'completed';
  completedDate?: string;
  attachments?: AttachmentFile[]; // 附件清單
}


@Component({
  selector: 'app-control-list',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe],
  templateUrl: './control-list.component.html',
  styleUrl: './control-list.component.scss'
})
export class ControlListComponent extends PermissionBaseComponent implements OnInit {
  controlItems: ControlItem[] = [];
  userList: { id: string; name: string; account: string }[] = []; // 使用者清單
  departments: Department[] = []; // 部門清單
  departmentsLoaded: boolean = false; // 部門載入狀態
  isLoadingUsers: boolean = false;
  isLoading: boolean = false;
  isSaving: boolean = false;
  uploadProgress: number = 0;
  errorMessage: string = '';
  successMessage: string = '';
  currentUserId: string = '';
  currentUserName: string = '';
  companyId: string = '';
  assistantNo: string = '';

  // 篩選和排序選項
  filterStatus: string = 'all';
  filterDepartmentId: string = 'all'; // 部門篩選
  sortBy: string = 'updatedDate'; // 預設按照修改日排序

  // 檢視模式
  viewMode: 'list' | 'calendar' = 'list';
  
  // 行事曆相關屬性
  currentDate: Date = new Date();
  calendarDays: any[] = [];
  weekDays: string[] = ['日', '一', '二', '三', '四', '五', '六'];

  // 新增項目的表單
  showAddForm: boolean = false;
  newItem: ControlItem = {
    id: '',
    companyid: '',
    assistantNo: '',
    title: '',
    description: '',
    dueDate: '',
    assignedTo: '',
    priority: 'medium',
    status: 'pending'
  };

  // 編輯項目
  editingItem: ControlItem | null = null;
  showEditForm: boolean = false; // 控制編輯彈出視窗
  selectedFiles: File[] = []; // 暫存選擇的檔案

  constructor(
    permissionService: ComponentPermissionService,
    private databaseService: DatabaseService,
    private controlItemService: ControlItemService,
    private authService: AuthService,
    private guidService: GuidService,
    private fileUploadService: FileUploadService,
    private i18nService: I18nService,
    private assistantUrlService: AssistantUrlService,
    private departmentService: DepartmentService
  ) {
    super(permissionService);
  }

  // 便利的翻譯函數
  private get translate() {
    return this.i18nService;
  }

  async ngOnInit(): Promise<void> {
    this.initializeUserInfo();

    // 載入元件權限（componentName 必須與 modules 設定中的 component_name 一致）
    await this.loadPermissions('controlListComponent');

    this.loadControlItems();
    this.loadUsers();
    this.loadDepartments(); // 載入部門清單
    setTimeout(() => {
      this.generateCalendar();
    }, 100);
  }

  // 初始化使用者資訊
  initializeUserInfo() {
    this.companyId = environment.companyid || localStorage.getItem('companyid') || '';
    this.currentUserId = localStorage.getItem('userid') || '';
    this.currentUserName = localStorage.getItem('username') || '';
    // 從 URL 參數取得 assistantNo
    this.assistantNo = this.assistantUrlService.getAssistantIdFromUrl();
  }

  // 載入控制清單項目
  loadControlItems() {
    if (!this.companyId) {
      this.errorMessage = this.translate.translate('control_list.message.company_info_error');
      return;
    }

    console.log('正在載入控制項目，companyId:', this.companyId);
    this.isLoading = true;
    this.errorMessage = '';

    const params: ControlItemQueryParams = {
      assistantNo: this.assistantNo,
      isActive: true
    };

    // === 加入 Data Scope 參數 ===
    const userInfo = this.authService.getUserInfo();
    if (this.componentPermission?.dataScope) {
      (params as any).userid = userInfo.userid;
      (params as any).dataScope = this.componentPermission.dataScope;
      console.log('[DataScope] 資料範圍權限:', this.componentPermission.dataScope);
    }

    console.log('查詢參數:', params);

    this.controlItemService.getControlItems(params).pipe(
      finalize(() => this.isLoading = false)
    ).subscribe({
      next: (response) => {
        console.log('API 回應:', response);
        if (response.success && response.data) {
          // 轉換資料格式以相容於現有元件
          this.controlItems = (response.data as ControlItemModel[]).map(item => ({
            id: item.id, // 保持原始 ID 格式
            companyid: item.companyid,
            assistantNo: item.assistantNo,
            title: item.title,
            description: item.description,
            dueDate: item.dueDate,
            assignedTo: item.assignedTo,
            departmentId: (item as any).departmentId || '', // 部門 ID
            departmentName: (item as any).departmentName || '', // 部門名稱
            priority: item.priority,
            status: item.status as 'pending' | 'inProgress' | 'completed',
            completedDate: item.completedDate,
            attachments: item.attachments,
            _ts: (item as any)._ts // 保留 _ts 欄位用於排序
          }));

          // 在前端按照 _ts (更新時間戳記) 降序排序，最新更新的項目在最前面
          this.controlItems.sort((a: any, b: any) => {
            const tsA = a._ts || 0;
            const tsB = b._ts || 0;
            return tsB - tsA; // 降序排序
          });

          console.log('轉換並排序後的控制項目:', this.controlItems);
        } else {
          console.log('API 回應不包含資料，使用預設資料');
          // 如果沒有資料，使用預設資料
          this.loadDefaultItems();
        }
      },
      error: (error) => {
        console.error('載入控制項目失敗:', error);
        this.errorMessage = this.translate.translate('control_list.message.load_error') + ': ' + (error.message || error);
        // 載入失敗時使用預設資料
        this.loadDefaultItems();
      }
    });
  }

  // 載入預設資料
  private loadDefaultItems() {
    this.controlItems = [
      {
        id: 'default-1',
        companyid: this.companyId,
        assistantNo: this.assistantNo,
        title: '公共危險品申報',
        description: '依法規要求完成每季公共危險品的申報作業',
        dueDate: '2025-05-15',
        assignedTo: '李安全',
        priority: 'high',
        status: 'pending'
      },
      {
        id: 'default-2',
        companyid: this.companyId,
        assistantNo: this.assistantNo,
        title: 'SDS文件建置',
        description: '完成所有化學品的SDS文件建置和更新',
        dueDate: '2025-06-30',
        assignedTo: '張工程',
        priority: 'medium',
        status: 'inProgress'
      }
    ];
  }

  // 過濾項目
  getFilteredItems() {
    let items = [...this.controlItems];

    // 按狀態過濾
    if (this.filterStatus !== 'all') {
      items = items.filter(item => item.status === this.filterStatus);
    }

    // 按部門過濾
    if (this.filterDepartmentId !== 'all') {
      items = items.filter(item => item.departmentId === this.filterDepartmentId);
    }

    // 排序
    items.sort((a, b) => {
      if (this.sortBy === 'updatedDate') {
        // 按照 _ts 修改日期降序排序（最新的在前面）
        const tsA = (a as any)._ts || 0;
        const tsB = (b as any)._ts || 0;
        return tsB - tsA;
      } else if (this.sortBy === 'dueDate') {
        return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
      } else if (this.sortBy === 'priority') {
        const priorityValue = { low: 0, medium: 1, high: 2 };
        return priorityValue[b.priority] - priorityValue[a.priority];
      }
      return 0;
    });

    return items;
  }

  // 開啟新增表單
  openAddForm() {
    // 權限檢查：需要 create 權限
    if (!this.hasPermission('create')) {
      this.errorMessage = this.translate.translate('control_list.message.no_create_permission');
      return;
    }

    this.showAddForm = true;
    this.newItem = {
      id: '', // 新項目 ID 由後端產生
      companyid: this.companyId,
      assistantNo: this.assistantNo,
      title: '',
      description: '',
      dueDate: '',
      assignedTo: '',
      departmentId: '', // 所屬部門 ID
      departmentName: '',
      priority: 'medium',
      status: 'pending'
    };
  }

  // 取消新增
  cancelAdd() {
    this.showAddForm = false;
  }

  // 提交新項目
  submitNewItem() {
    if (!this.newItem.title || !this.newItem.dueDate) {
      this.errorMessage = this.translate.translate('control_list.message.required_fields');
      return;
    }

    this.isSaving = true;
    this.errorMessage = '';
    this.successMessage = '';

    // 根據選擇的部門 ID 取得部門名稱
    const selectedDept = this.departments.find(d => d.id === this.newItem.departmentId);

    const createRequest = {
      title: this.newItem.title,
      description: this.newItem.description || '',
      dueDate: this.newItem.dueDate,
      assignedTo: this.newItem.assignedTo || '',
      departmentId: this.newItem.departmentId || '', // 部門 ID
      departmentName: selectedDept?.name || '', // 部門名稱
      priority: this.newItem.priority,
      status: this.newItem.status as 'pending' | 'inProgress' | 'completed',
      created_by: this.currentUserId,
      assistantNo: this.assistantNo
    };

    this.controlItemService.createControlItem(createRequest).pipe(
      finalize(() => {
        this.isSaving = false;
        this.showAddForm = false;
      })
    ).subscribe({
      next: (response) => {
        if (response.success) {
          this.successMessage = this.translate.translate('control_list.message.add_success');
          this.loadControlItems(); // 重新載入清單
        } else {
          this.errorMessage = response.error || this.translate.translate('control_list.message.add_error');
        }
      },
      error: (error) => {
        console.error('新增項目失敗:', error);
        this.errorMessage = this.translate.translate('control_list.message.add_error');
      }
    });
  }

  // 獲取下一個ID（已不使用，ID 由後端生成）
  private getNextId(): string {
    return ''; // ID 由後端 GUID 生成，前端不需要處理
  }

  // 編輯項目
  editItem(item: ControlItem) {
    // 權限檢查：需要 edit 權限
    if (!this.hasPermission('edit')) {
      this.errorMessage = this.translate.translate('control_list.message.no_edit_permission');
      return;
    }

    this.editingItem = {
      ...item,
      attachments: item.attachments || []
    };
    this.selectedFiles = [];
    this.showEditForm = true;
  }

  // 取消編輯
  cancelEdit() {
    this.editingItem = null;
    this.showEditForm = false;
    this.selectedFiles = [];
  }

  // 保存編輯
  saveEdit() {
    if (!this.editingItem) return;

    this.isSaving = true;
    this.errorMessage = '';
    this.successMessage = '';

    // 找到原始項目以取得完整ID
    const originalItem = this.controlItems.find(item => item.id === this.editingItem!.id);
    if (!originalItem) {
      this.errorMessage = this.translate.translate('control_list.message.item_not_found');
      this.isSaving = false;
      return;
    }

    // 根據選擇的部門 ID 取得部門名稱
    const selectedDept = this.departments.find(d => d.id === this.editingItem!.departmentId);

    const updateRequest = {
      title: this.editingItem.title,
      description: this.editingItem.description,
      dueDate: this.editingItem.dueDate,
      assignedTo: this.editingItem.assignedTo,
      departmentId: this.editingItem.departmentId || '', // 部門 ID
      departmentName: selectedDept?.name || '', // 部門名稱
      priority: this.editingItem.priority,
      status: this.editingItem.status as 'pending' | 'inProgress' | 'completed',
      completedDate: this.editingItem.status === 'completed' ? new Date().toISOString().split('T')[0] : undefined,
      updated_by: this.currentUserId
    };

    // 檢查是否需要上傳檔案
    const needsFileUpload = this.selectedFiles && this.selectedFiles.length > 0 && this.editingItem.status === 'completed';
    
    console.log('檔案上傳檢查:', {
      selectedFilesCount: this.selectedFiles?.length || 0,
      status: this.editingItem.status,
      needsFileUpload: needsFileUpload
    });

    if (needsFileUpload) {
      // 有選擇檔案且狀態為已完成，讓 ControlItemService 處理檔案上傳
      this.updateControlItemWithFiles(originalItem.id.toString(), updateRequest, this.selectedFiles);
    } else {
      // 沒有檔案需要上傳，直接更新控制項目
      this.updateControlItem(originalItem.id.toString(), updateRequest);
    }
  }

  // 更新控制項目
  private updateControlItem(id: string, updateRequest: any) {
    this.controlItemService.updateControlItem(id, updateRequest).pipe(
      finalize(() => {
        this.isSaving = false;
        this.showEditForm = false;
        this.editingItem = null;
        this.selectedFiles = [];
        this.uploadProgress = 0;
      })
    ).subscribe({
      next: (response) => {
        if (response.success) {
          this.successMessage = this.translate.translate('control_list.message.update_success');
          this.loadControlItems();
        } else {
          this.errorMessage = response.error || this.translate.translate('control_list.message.update_error');
        }
      },
      error: (error) => {
        console.error('更新項目失敗:', error);
        this.errorMessage = this.translate.translate('control_list.message.update_error');
      }
    });
  }

  // 更新控制項目並上傳檔案
  private updateControlItemWithFiles(id: string, updateRequest: any, files: File[]) {
    this.uploadProgress = 0;
    this.controlItemService.updateControlItem(id, updateRequest, files).pipe(
      finalize(() => {
        this.isSaving = false;
        this.showEditForm = false;
        this.editingItem = null;
        this.selectedFiles = [];
        this.uploadProgress = 0;
      })
    ).subscribe({
      next: (response) => {
        if (response.success) {
          this.successMessage = this.translate.translate('control_list.message.update_with_files_success');
          this.loadControlItems();
        } else {
          this.errorMessage = response.error || this.translate.translate('control_list.message.update_error');
        }
      },
      error: (error) => {
        console.error('更新項目失敗:', error);
        this.errorMessage = this.translate.translate('control_list.message.update_error') + ': ' + (error.message || error);
      }
    });
  }

  // 刪除項目
  deleteItem(id: string) {
    // 權限檢查：需要 delete 權限
    if (!this.hasPermission('delete')) {
      this.errorMessage = this.translate.translate('control_list.message.no_delete_permission');
      return;
    }

    if (confirm(this.translate.translate('control_list.confirm_delete'))) {
      this.isLoading = true;
      this.errorMessage = '';

      this.controlItemService.deleteControlItem(id, this.currentUserId).pipe(
        finalize(() => this.isLoading = false)
      ).subscribe({
        next: (response) => {
          if (response.success) {
            this.successMessage = this.translate.translate('control_list.message.delete_success');
            this.loadControlItems();
          } else {
            this.errorMessage = response.error || this.translate.translate('control_list.message.delete_error');
          }
        },
        error: (error) => {
          console.error('刪除項目失敗:', error);
          this.errorMessage = this.translate.translate('control_list.message.delete_error');
        }
      });
    }
  }

  // 更改項目狀態
  changeStatus(item: ControlItem, status: 'pending' | 'inProgress' | 'completed') {
    const index = this.controlItems.findIndex(i => i.id === item.id);
    if (index !== -1) {
      this.controlItems[index].status = status;

      // 如果標記為已完成，添加完成日期
      if (status === 'completed') {
        this.controlItems[index].completedDate = new Date().toISOString().split('T')[0];
      } else {
        delete this.controlItems[index].completedDate;
      }

      // 在實際應用中，這裡應該調用服務將狀態更新保存到後端
    }
  }

  // 獲取優先級標籤的CSS類
  getPriorityClass(priority: string): string {
    switch (priority) {
      case 'high': return 'priority-high';
      case 'medium': return 'priority-medium';
      case 'low': return 'priority-low';
      default: return '';
    }
  }

  // 獲取狀態標籤的CSS類
  getStatusClass(status: string): string {
    switch (status) {
      case 'pending': return 'status-pending';
      case 'inProgress': return 'status-inprogress';
      case 'completed': return 'status-completed';
      default: return '';
    }
  }

  // 獲取優先級翻譯文字
  getPriorityText(priority: string): string {
    switch (priority) {
      case 'high': return this.translate.translate('control_list.priority.high');
      case 'medium': return this.translate.translate('control_list.priority.medium');
      case 'low': return this.translate.translate('control_list.priority.low');
      default: return priority;
    }
  }

  // 獲取狀態翻譯文字
  getStatusText(status: string): string {
    switch (status) {
      case 'pending': return this.translate.translate('control_list.status.pending');
      case 'inProgress': return this.translate.translate('control_list.status.in_progress');
      case 'completed': return this.translate.translate('control_list.status.completed');
      default: return status;
    }
  }

  // 載入使用者清單
  loadUsers() {
    this.isLoadingUsers = true;
    const filter = {};

    this.databaseService.getDataByQuery(filter, 1000, "users").pipe(
      map((response: any) => {
        if (!response?.items) {
          return [];
        }
        // 將使用者資料轉換為簡化的格式
        return response.items.map((user: any) => ({
          id: user.id,
          name: user.name || user.username || '未命名',
          account: user.account || user.email || ''
        }));
      }),
      catchError(error => {
        console.error('載入使用者清單失敗:', error);
        // 如果載入失敗，使用預設值
        return of([
          { id: 'default1', name: '系統管理員', account: 'admin' },
          { id: 'default2', name: '訪客', account: 'guest' }
        ]);
      })
    ).subscribe(users => {
      this.userList = users;
      this.isLoadingUsers = false;
      console.log('載入的使用者清單:', this.userList);
    });
  }

  /**
   * 載入部門清單（依權限過濾）
   */
  loadDepartments(): void {
    console.log('[ControlList] 載入部門資料...');

    // 取得使用者資訊和助手編號
    const userInfo = this.authService.getUserInfo();

    // 建立查詢選項
    const options: any = { enabledOnly: true };

    // 如果有權限限制（非 'all'），加入權限參數
    if (this.componentPermission?.dataScope && this.componentPermission.dataScope !== 'all') {
      options.dataScope = this.componentPermission.dataScope;
      options.userid = userInfo.userid;
      options.assistantNo = this.assistantNo;
      console.log('[ControlList] 使用權限過濾載入部門:', options);
    }

    // 呼叫 DepartmentService
    this.departmentService.getDepartmentList(options)
      .subscribe({
        next: (departments) => {
          console.log('[ControlList] 部門資料載入成功:', departments);
          this.departments = departments;
          this.departmentsLoaded = true;
        },
        error: (error) => {
          console.error('[ControlList] 部門資料載入失敗:', error);
          this.departments = [];
          this.departmentsLoaded = true;
        }
      });
  }

  // 處理檔案選擇
  onFileSelected(event: any) {
    const files: FileList = event.target.files;
    if (files) {
      this.selectedFiles = Array.from(files);
    }
  }

  // 移除已選擇的檔案
  removeSelectedFile(index: number) {
    this.selectedFiles.splice(index, 1);
  }

  // 移除已上傳的附件
  removeAttachment(attachmentId: string) {
    if (this.editingItem && this.editingItem.attachments) {
      if (confirm(this.translate.translate('control_list.message.confirm_remove_file'))) {
        // 先從本地移除
        this.editingItem.attachments = this.editingItem.attachments.filter(
          att => att.id !== attachmentId
        );
        // 注意：實際刪除會在保存時執行
      }
    }
  }

  // 格式化檔案大小
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 ' + this.translate.translate('control_list.file_size.bytes');
    const k = 1024;
    const sizeLabels = [
      this.translate.translate('control_list.file_size.bytes'),
      this.translate.translate('control_list.file_size.kb'),
      this.translate.translate('control_list.file_size.mb'),
      this.translate.translate('control_list.file_size.gb')
    ];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizeLabels[i];
  }

  // 切換檢視模式
  switchViewMode(mode: 'list' | 'calendar') {
    this.viewMode = mode;
    if (mode === 'calendar') {
      setTimeout(() => {
        this.generateCalendar();
      }, 50);
    }
  }

  // 生成行事曆
  generateCalendar() {
    console.log('生成行事曆中...');
    const year = this.currentDate.getFullYear();
    const month = this.currentDate.getMonth();
    
    // 取得當月第一天和最後一天
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    // 取得第一天是星期幾
    const startDayOfWeek = firstDay.getDay();
    
    // 取得當月天數
    const daysInMonth = lastDay.getDate();
    
    this.calendarDays = [];
    console.log(`生成 ${year}年${month + 1}月 行事曆，共 ${daysInMonth} 天`);
    
    // 加入上個月的日期來填滿第一週
    const prevMonth = new Date(year, month - 1, 0);
    for (let i = startDayOfWeek - 1; i >= 0; i--) {
      const day = prevMonth.getDate() - i;
      this.calendarDays.push({
        date: new Date(year, month - 1, day),
        day: day,
        isCurrentMonth: false,
        items: []
      });
    }
    
    // 當月的日期
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const items = this.getItemsForDate(date);
      this.calendarDays.push({
        date: date,
        day: day,
        isCurrentMonth: true,
        items: items
      });
    }
    
    // 填滿剩餘的格子到6週 (42格)
    const remainingDays = 42 - this.calendarDays.length;
    for (let day = 1; day <= remainingDays; day++) {
      this.calendarDays.push({
        date: new Date(year, month + 1, day),
        day: day,
        isCurrentMonth: false,
        items: []
      });
    }
    
    console.log(`行事曆生成完成，共 ${this.calendarDays.length} 格`);
  }

  // 取得指定日期的控制項目
  getItemsForDate(date: Date): ControlItem[] {
    const dateStr = this.formatDateForComparison(date);
    return this.getFilteredItems().filter(item => {
      const itemDateStr = this.formatDateForComparison(new Date(item.dueDate));
      return itemDateStr === dateStr;
    });
  }

  // 格式化日期用於比較 (YYYY-MM-DD)
  private formatDateForComparison(date: Date): string {
    return date.getFullYear() + '-' + 
           String(date.getMonth() + 1).padStart(2, '0') + '-' + 
           String(date.getDate()).padStart(2, '0');
  }

  // 上一個月
  previousMonth() {
    this.currentDate = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() - 1, 1);
    this.generateCalendar();
  }

  // 下一個月
  nextMonth() {
    this.currentDate = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() + 1, 1);
    this.generateCalendar();
  }

  // 取得月份年份顯示文字
  getMonthYearText(): string {
    const year = this.currentDate.getFullYear();
    const month = this.currentDate.getMonth() + 1;
    return this.translate.translate('control_list.calendar.month_year', { year, month });
  }

  // 取得翻譯後的星期標題
  getWeekDaysTranslated(): string[] {
    return [
      this.translate.translate('control_list.calendar.weekdays.sun'),
      this.translate.translate('control_list.calendar.weekdays.mon'),
      this.translate.translate('control_list.calendar.weekdays.tue'),
      this.translate.translate('control_list.calendar.weekdays.wed'),
      this.translate.translate('control_list.calendar.weekdays.thu'),
      this.translate.translate('control_list.calendar.weekdays.fri'),
      this.translate.translate('control_list.calendar.weekdays.sat')
    ];
  }

  // 取得今天日期
  isToday(date: Date): boolean {
    const today = new Date();
    return this.formatDateForComparison(date) === this.formatDateForComparison(today);
  }

  // 在行事曆上點擊項目
  onCalendarItemClick(item: ControlItem) {
    this.editItem(item);
  }

  // 生成檔案ID
  private generateFileId(): string {
    return 'file_' + Date.now() + '_' + Math.random().toString(36).substring(2, 11);
  }

  // 格式化部門名稱（加入階層縮排）
  formatDepartmentName(department: Department): string {
    const indent = '　'.repeat(department.level); // 使用全形空格
    return `${indent}${department.name}`;
  }
}
